import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalBar as DrinkIcon,
  Restaurant as FoodIcon,
  MenuBook as CourseIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { DrinkFoodPlan } from '../types';
import PlanDialog from '../components/PlanDialog';

const Plans: React.FC = () => {
  const { shop } = useAuth();
  const [plans, setPlans] = useState<DrinkFoodPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<DrinkFoodPlan | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState<DrinkFoodPlan | null>(null);

  useEffect(() => {
    fetchPlans();
  }, [shop]);

  const fetchPlans = async () => {
    if (!shop) return;

    try {
      setLoading(true);
      const q = query(
        collection(db, 'drinkFoodPlans'),
        where('shopId', '==', shop.id)
      );
      const snapshot = await getDocs(q);
      const plansData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as DrinkFoodPlan[];

      // displayOrderでソート
      plansData.sort((a, b) => a.displayOrder - b.displayOrder);
      setPlans(plansData);
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('プランの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (plan?: DrinkFoodPlan) => {
    setSelectedPlan(plan || null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPlan(null);
  };

  const handleSaveSuccess = () => {
    setSuccess('プランを保存しました');
    setTimeout(() => setSuccess(null), 3000);
    fetchPlans();
    handleCloseDialog();
  };

  const handleDeleteClick = (plan: DrinkFoodPlan) => {
    setPlanToDelete(plan);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!planToDelete) return;

    try {
      await deleteDoc(doc(db, 'drinkFoodPlans', planToDelete.id));
      setSuccess('プランを削除しました');
      setTimeout(() => setSuccess(null), 3000);
      fetchPlans();
    } catch (err) {
      console.error('Error deleting plan:', err);
      setError('プランの削除に失敗しました');
    } finally {
      setDeleteDialogOpen(false);
      setPlanToDelete(null);
    }
  };

  const getPlanTypeIcon = (type: DrinkFoodPlan['type']) => {
    switch (type) {
      case 'all_you_can_drink':
        return <DrinkIcon />;
      case 'all_you_can_eat':
        return <FoodIcon />;
      case 'course':
        return <CourseIcon />;
    }
  };

  const getPlanTypeName = (type: DrinkFoodPlan['type']) => {
    switch (type) {
      case 'all_you_can_drink':
        return '飲み放題';
      case 'all_you_can_eat':
        return '食べ放題';
      case 'course':
        return 'コース';
    }
  };

  if (!shop) {
    return (
      <Container>
        <Typography>店舗情報を読み込み中...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">飲み放題・食べ放題プラン管理</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          プラン追加
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>種類</TableCell>
                <TableCell>プラン名</TableCell>
                <TableCell>価格</TableCell>
                <TableCell>制限時間</TableCell>
                <TableCell>ラストオーダー</TableCell>
                <TableCell>商品数</TableCell>
                <TableCell>継承元</TableCell>
                <TableCell>状態</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    読み込み中...
                  </TableCell>
                </TableRow>
              ) : plans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    プランが登録されていません
                  </TableCell>
                </TableRow>
              ) : (
                plans.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getPlanTypeIcon(plan.type)}
                        {getPlanTypeName(plan.type)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold">
                        {plan.name}
                      </Typography>
                      {plan.description && (
                        <Typography variant="caption" color="text.secondary">
                          {plan.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>¥{plan.price.toLocaleString()}</TableCell>
                    <TableCell>{plan.timeLimit}分</TableCell>
                    <TableCell>終了{plan.lastOrderBefore}分前</TableCell>
                    <TableCell>{plan.includedProductIds.length}品</TableCell>
                    <TableCell>
                      {plan.basePlanId ? (
                        <Typography variant="body2" color="primary">
                          {plans.find(p => p.id === plan.basePlanId)?.name || '不明'}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          なし
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={plan.isActive ? '有効' : '無効'}
                        color={plan.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(plan)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(plan)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* プラン作成・編集ダイアログ */}
      <PlanDialog
        open={dialogOpen}
        plan={selectedPlan}
        plans={plans}
        onClose={handleCloseDialog}
        onSuccess={handleSaveSuccess}
      />

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>プランの削除</DialogTitle>
        <DialogContent>
          <Typography>
            「{planToDelete?.name}」を削除してもよろしいですか？
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            ※ このプランを継承している他のプランがある場合、継承関係が解除されます。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            キャンセル
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Plans;
